// Broadcast — the internal/external newsroom backing /announcements.
//
// Announcements remain the canonical server entity. This module keeps the
// client-side view, authoring draft and optimistic reaction state together so
// the page, home widget and detail window all read one normalized shape.

const _audience = (value) => (value === 'external' ? 'external' : 'internal')

const _shellMe = (root) => {
  const email = (root?.currentUserEmail || '').toLowerCase()
  return (root?.people || []).find((person) => (person.email || '').toLowerCase() === email) || null
}

const _plainText = (body) =>
  (Array.isArray(body) ? body : [body])
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join('\n\n')

// Minutes a post takes to read: 200 words a minute, never under one. The
// record stores only this COUNT (`read_time`). `broadcastReadTime` renders
// the sentence around it in the viewer's language, so a post saved on a
// Georgian page does not read Georgian on an English one.
const _readMinutes = (body) => {
  const words = _plainText(body).split(/\s+/).filter(Boolean).length
  return Math.max(1, Math.ceil(words / 200))
}

const _samePost = (post, id) => String(post?.id) === String(id)

export const broadcastFromWire = function broadcastFromWire(row) {
  if (!row) return null
  return {
    id: row.id || row._id,
    title: row.title || '',
    author: row.author || '',
    authorAvatar: row.author_avatar || row.authorAvatar || '',
    authorRole: row.author_role || row.authorRole || '',
    date: row.date || '',
    readTime: row.read_time || row.readTime || '',
    // A row with no topic carries the studio's default-topic KEY: the filter
    // rail (broadcastTags) and the news pill (IntranetRows) resolve it through
    // polyglot, and a real topic passes through verbatim.
    tag: row.tag || 'workspace.broadcastStudio.preview.tagDefault',
    cover: row.cover || '',
    excerpt: row.excerpt || '',
    body: Array.isArray(row.body) ? row.body : row.body ? [row.body] : [],
    reactions: row.reactions || {},
    audience: _audience(row.audience),
    externalUrl: row.external_url || row.externalUrl || ''
  }
}

// el.call('broadcastReadTime', post) — the read-time line a post shows, in
// the viewer's language. The minutes come from the post's own body: a row
// saved before the record held a bare count carries the English sentence
// ("3 min read"), and its body gives the same number. A post with no stored
// read time shows none, as before.
export const broadcastReadTime = function broadcastReadTime(post) {
  if (!post || !post.readTime) return ''
  return String(this.call('polyglot', 'workspace.broadcast.readTime')).replace(
    '{count}',
    String(_readMinutes(post.body))
  )
}

export const broadcastItemsForView = function broadcastItemsForView(root) {
  const scope = root?._broadcastScope || 'internal'
  const tag = root?._broadcastTag || 'all'
  const query = (root?._broadcastQuery || '').trim().toLowerCase()

  return (root?.announcements || [])
    .filter((post) => {
      const audience = _audience(post?.audience)
      if (scope !== 'all' && audience !== scope) return false
      if (tag !== 'all' && String(post?.tag || '').toLowerCase() !== tag) return false
      if (!query) return true
      return [post?.title, post?.excerpt, post?.author, post?.tag, _plainText(post?.body)]
        .join(' ')
        .toLowerCase()
        .includes(query)
    })
    .sort((a, b) => String(b?.date || '').localeCompare(String(a?.date || '')))
}

export const broadcastVisibleItems = function broadcastVisibleItems(root) {
  const limit = Math.max(1, Number(root?._broadcastVisibleCount) || 50)
  return broadcastItemsForView(root).slice(0, limit)
}

export const broadcastTags = function broadcastTags(root) {
  const counts = new Map()
  for (const post of root?.announcements || []) {
    const tag = String(post?.tag || '').trim() || 'workspace.broadcastStudio.preview.tagDefault'
    const key = tag.toLowerCase()
    counts.set(key, { key, label: tag, count: (counts.get(key)?.count || 0) + 1 })
  }
  return [
    {
      key: 'all',
      label: 'workspace.broadcastStudio.tag.all',
      count: (root?.announcements || []).length
    },
    ...counts.values()
  ]
}

// Home BroadcastWidget unread count (board §2 "broadcast: latest 2-3
// colored rows (exists) + unread count"). No server-side read-tracking
// exists for announcements (verified — neither a DB column nor a client
// preference), so this is a purely client-local, SESSION-scoped "seen"
// marker: everything newer than `_broadcastLastSeenAt` counts as unread,
// and posts that predate the viewer's first visit this session default to
// unread too (the standard "you haven't looked yet" badge behaviour, same
// as an unopened inbox). `_broadcastLastSeenAt` is an ephemeral root key —
// same convention as the home board's other UI-only keys (`_reminderDraft`,
// `_homeCustomizeMode`, `_createAgentOpen` — none pre-declared in
// state.js) — never persisted, never fetched.
export const broadcastUnreadCount = function broadcastUnreadCount(root) {
  const seenAt = Number(root?._broadcastLastSeenAt) || 0
  return (root?.announcements || []).filter((post) => {
    const t = post?.date ? new Date(post.date).getTime() : 0
    return t > seenAt
  }).length
}

// Marks the current moment as "seen" — called from the widget header's
// action button and from a click anywhere inside its News list (event
// bubbles up from BroadcastNewsRow, whose own onClick can't be overridden
// via childExtends in v3.14 — see that component's file-header comment —
// so the listener lives one level up, on the News container itself).
export const markBroadcastSeen = function markBroadcastSeen() {
  const root = this?.state?.root
  if (!root) return
  root.update({ _broadcastLastSeenAt: Date.now() }, { preventFetch: true })
}

export const setBroadcastScope = function setBroadcastScope(scope) {
  const root = this?.state?.root
  if (!root) return
  root.update({
    _broadcastScope: scope === 'external' || scope === 'all' ? scope : 'internal',
    _broadcastVisibleCount: 50
  })
}

export const setBroadcastTag = function setBroadcastTag(tag) {
  const root = this?.state?.root
  if (!root) return
  root.update({ _broadcastTag: tag || 'all', _broadcastVisibleCount: 50 })
}

export const setBroadcastQuery = function setBroadcastQuery(query) {
  const root = this?.state?.root
  if (!root) return
  root.update({ _broadcastQuery: String(query || ''), _broadcastVisibleCount: 50 })
}

export const broadcastHasActiveFilters = function broadcastHasActiveFilters(root) {
  return !!String(root?._broadcastQuery || '').trim() || (root?._broadcastTag || 'all') !== 'all'
}

export const clearBroadcastFilters = function clearBroadcastFilters() {
  const root = this?.state?.root
  if (!root) return
  root.update({ _broadcastQuery: '', _broadcastTag: 'all', _broadcastVisibleCount: 50 })
}

export const showMoreBroadcastItems = function showMoreBroadcastItems() {
  const root = this?.state?.root
  if (!root) return
  root.update({ _broadcastVisibleCount: (Number(root._broadcastVisibleCount) || 50) + 50 })
}

export const canPublishBroadcast = function canPublishBroadcast(rootArg) {
  const root = rootArg || this?.state?.root
  if (!root) return false
  return (
    this.call('hasPermission', root, 'stories.post') ||
    root.org?.role === 'owner' ||
    root.org?.role === 'admin'
  )
}

export const canEditBroadcast = function canEditBroadcast(post, rootArg) {
  const root = rootArg || this?.state?.root
  if (!root || !post) return false
  if (this.call('hasPermission', root, 'stories.edit_others')) return true
  if (!this.call('canPublishBroadcast', root)) return false
  const me = _shellMe(root)
  const meName = (me?.name || '').trim().toLowerCase()
  const meEmail = (root.currentUserEmail || '').trim().toLowerCase()
  const author = (post.author || '').trim().toLowerCase()
  return Boolean(author && (author === meName || author === meEmail))
}

export const openBroadcastDetail = function openBroadcastDetail(post) {
  const root = this?.state?.root
  if (!root || !post) return false
  root.update({ selectedAnnouncement: post })
  this.call('openModal', {
    id: 'announcement-detail',
    template: 'custom',
    width: 'G1',
    maxHeight: '85vh',
    content: { extends: 'AnnouncementDetailModal' }
  })
  return true
}

export const openBroadcastComposer = function openBroadcastComposer(post) {
  const root = this?.state?.root
  if (!root || (!post && !this.call('canPublishBroadcast', root))) return
  if (post && !this.call('canEditBroadcast', post, root)) return

  root.update({
    _broadcastDraft: post
      ? {
          id: post.id,
          title: post.title || '',
          excerpt: post.excerpt || '',
          body: _plainText(post.body),
          // The topic input shows this value: resolve a stored key to its word.
          tag: this.call('polyglot', post.tag || 'workspace.broadcastStudio.preview.tagDefault'),
          audience: _audience(post.audience),
          externalUrl: post.externalUrl || ''
        }
      : {
          id: null,
          title: '',
          excerpt: '',
          body: '',
          tag: this.call('polyglot', 'workspace.broadcastStudio.preview.tagDefault'),
          audience: root._broadcastScope === 'external' ? 'external' : 'internal',
          externalUrl: ''
        },
    _broadcastSaveError: null
  })
  // A FORM window: the form-modal width every workspace form takes ('I',
  // capped at the viewport by the layer). It was 'G1' — the read card's width
  // — and opened 355 px wide at 1440, its headline field cutting its own
  // placeholder (dev 9b9291d4e, 2026-10-02).
  this.call('openModal', {
    id: 'broadcast-compose',
    template: 'custom',
    width: 'I',
    maxWidth: '90vw',
    maxHeight: '85vh',
    content: { extends: 'BroadcastComposerModal' }
  })
}

export const setBroadcastDraft = function setBroadcastDraft(field, value) {
  const root = this?.state?.root
  if (!root || !root._broadcastDraft || !field) return
  root.update({ _broadcastDraft: { ...root._broadcastDraft, [field]: String(value || '') } })
}

// `opts.check` — validate only: the draft's problem (no title / no body / a bad
// external URL) is shown exactly as a save would show it, and nothing is sent.
// submitBroadcastDraft runs it before it asks, so the person never confirms a
// publish that cannot be stored.
export const saveBroadcastDraft = async function saveBroadcastDraft(opts) {
  const root = this?.state?.root
  const sdk = this?.sdk || this?.context?.sdk
  const draft = root?._broadcastDraft
  if (!root || !draft || root._broadcastSaving || !sdk?.execute) return false

  const title = String(draft.title || '').trim()
  const body = String(draft.body || '')
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
  const audience = _audience(draft.audience)
  let externalUrl = String(draft.externalUrl || '').trim()

  if (!title) {
    root.update({
      _broadcastSaveError: this.call('polyglot', 'workspace.broadcastStudio.error.noTitle')
    })
    return false
  }
  if (!body.length) {
    root.update({
      _broadcastSaveError: this.call('polyglot', 'workspace.broadcastStudio.error.noBody')
    })
    return false
  }
  if (audience === 'external' && externalUrl) {
    try {
      externalUrl = new URL(
        externalUrl.startsWith('http') ? externalUrl : `https://${externalUrl}`
      ).toString()
    } catch (_) {
      root.update({
        _broadcastSaveError: this.call('polyglot', 'workspace.broadcastStudio.error.badUrl')
      })
      return false
    }
  } else if (audience === 'internal') {
    externalUrl = ''
  }
  if (opts && opts.check) {
    if (root._broadcastSaveError) root.update({ _broadcastSaveError: null })
    return true
  }

  const me = _shellMe(root)
  const payload = {
    title,
    excerpt: String(draft.excerpt || '').trim() || body[0],
    body,
    // Persisted: the WORD the author saw as the topic placeholder, never a
    // key — other surfaces read the stored topic as it is.
    tag:
      String(draft.tag || '').trim() ||
      this.call('polyglot', 'workspace.broadcastStudio.preview.tagDefault'),
    audience,
    external_url: externalUrl || null,
    author:
      me?.name ||
      root.currentUserEmail ||
      this.call('polyglot', 'workspace.broadcastStudio.authorFallback'),
    author_avatar: me?.avatar || me?.avatarUrl || null,
    author_role: me?.role || null,
    date: new Date().toISOString().slice(0, 10),
    read_time: String(_readMinutes(body))
  }

  const write = this.call('sameWorkspaceWriter', root)
  root.update({ _broadcastSaving: true, _broadcastSaveError: null })
  try {
    const operation = draft.id ? 'update' : 'create'
    const result = await sdk.execute('workspaceProject.announcements', operation, {
      ...(draft.id ? { id: draft.id } : {}),
      payload
    })
    const saved = this.call('broadcastFromWire', result?.announcement || result)
    // A bundle KEY as the message — the catch below resolves it.
    if (!saved?.id) throw new Error('workspace.broadcastStudio.error.noId')
    const exists = (root.announcements || []).some((post) => _samePost(post, saved.id))
    if (
      !write({
        announcements: exists
          ? (root.announcements || []).map((post) => (_samePost(post, saved.id) ? saved : post))
          : [saved, ...(root.announcements || [])],
        _broadcastSaving: false,
        _broadcastDraft: null
      })
    )
      return false
    this.call('closeModal', 'broadcast-compose')
    return true
  } catch (err) {
    write({
      _broadcastSaving: false,
      _broadcastSaveError: this.call(
        'polyglot',
        err?.message || 'workspace.broadcastStudio.error.publishFailed'
      )
    })
    return false
  }
}

export const toggleBroadcastReaction = async function toggleBroadcastReaction(id, emoji) {
  const root = this?.state?.root
  const sdk = this?.sdk || this?.context?.sdk
  if (!root || !id || !emoji || !sdk?.execute) return false
  const write = this.call('sameWorkspaceWriter', root)
  const before = root.announcements || []
  const me = _shellMe(root)
  const name =
    me?.name ||
    root.currentUserEmail ||
    this.call('polyglot', 'workspace.broadcastStudio.reactionYou')
  const optimistic = before.map((post) => {
    if (!_samePost(post, id)) return post
    const reactions = { ...(post.reactions || {}) }
    const next = Array.isArray(reactions[emoji]) ? [...reactions[emoji]] : []
    const index = next.indexOf(name)
    if (index === -1) next.push(name)
    else next.splice(index, 1)
    if (next.length) reactions[emoji] = next
    else delete reactions[emoji]
    return { ...post, reactions }
  })
  root.update({ announcements: optimistic })
  try {
    const result = await sdk.execute('workspaceProject.announcements', 'react', { id, emoji })
    const saved = this.call('broadcastFromWire', result?.announcement || result)
    if (saved?.id) {
      return write({
        announcements: (root.announcements || []).map((post) =>
          _samePost(post, saved.id) ? saved : post
        )
      })
    }
    return true
  } catch (err) {
    write({
      announcements: before,
      _broadcastSaveError:
        err?.message || this.call('polyglot', 'workspace.broadcastStudio.error.reactionFailed')
    })
    return false
  }
}

// The composer's submit (components/BroadcastStudio.js). A NEW post reaches its
// whole audience the moment it is stored (CRUD standard rule 1 "send to many
// people", rule 4), so Publish asks first: the title names the post and the
// audience, the message is that audience's own hint, the button is the
// composer's own verb ("Publish update"). The draft is checked BEFORE the ask
// (saveBroadcastDraft `check`). An edit of a published post ("Save changes")
// saves as before, unless it moves the post to the external audience: that
// reaches new people, so it asks the same way, with "Save changes" on the
// button. `onStored` runs only once the post is stored: the form passes its
// markPageSaved there.
export const submitBroadcastDraft = async function submitBroadcastDraft(onStored) {
  const root = this?.state?.root
  const draft = root?._broadcastDraft
  if (!root || !draft || root._broadcastSaving) return false
  const save = async () => {
    const stored = await this.call('saveBroadcastDraft')
    if (stored && typeof onStored === 'function') onStored()
    return stored
  }
  const external = _audience(draft.audience) === 'external'
  if (draft.id) {
    const was = (root.announcements || []).find((post) => _samePost(post, draft.id))
    if (!(external && was && _audience(was.audience) !== 'external')) return save()
  }
  if (!(await this.call('saveBroadcastDraft', { check: true }))) return false
  const title = String(draft.title || '').trim()
  this.call('confirmDialog', {
    title: this.call(
      'polyglot',
      external
        ? 'workspace.broadcastStudio.publish.titleExternal'
        : 'workspace.broadcastStudio.publish.titleWorkspace'
    ).replace('{title}', () => title),
    message: this.call(
      'polyglot',
      external
        ? 'workspace.broadcastStudio.composer.externalHint'
        : 'workspace.broadcastStudio.composer.workspaceHint'
    ),
    confirmText: this.call(
      'polyglot',
      draft.id
        ? 'workspace.broadcastStudio.composer.saveChanges'
        : 'workspace.broadcastStudio.composer.publish'
    ),
    cancelText: this.call('polyglot', 'common.action.cancel'),
    confirmTheme: 'primary',
    // `null` is the explicit opt-out; OMITTING the key takes the template's
    // 'warning' default.
    icon: null,
    onConfirm: save
  })
  return false
}

export const confirmDeleteBroadcast = function confirmDeleteBroadcast(post) {
  const root = this?.state?.root
  if (!post || !this.call('canEditBroadcast', post, root)) return
  this.call('confirmDialog', {
    title: this.call('polyglot', 'workspace.broadcastStudio.delete.title'),
    message: this.call('polyglot', 'workspace.broadcastStudio.delete.message'),
    confirmText: this.call('polyglot', 'workspace.broadcastStudio.delete.confirm'),
    cancelText: this.call('polyglot', 'common.action.cancel'),
    // D1 house shape ('danger' is no brand theme: the button had no style).
    confirmTheme: 'primary',
    icon: null,
    onConfirm: () => this.call('deleteBroadcast', post.id)
  })
}

export const deleteBroadcast = async function deleteBroadcast(id) {
  const root = this?.state?.root
  const sdk = this?.sdk || this?.context?.sdk
  if (!root || !id || !sdk?.execute) return false
  const write = this.call('sameWorkspaceWriter', root)
  const before = root.announcements || []
  root.update({ announcements: before.filter((post) => !_samePost(post, id)) })
  try {
    await sdk.execute('workspaceProject.announcements', 'remove', { id })
    return true
  } catch (err) {
    write({
      announcements: before,
      _broadcastSaveError:
        err?.message || this.call('polyglot', 'workspace.broadcastStudio.error.deleteFailed')
    })
    return false
  }
}
